// services/empresaService.ts
import { apiClient } from '../config/api';
import { Empresa, ApiResponse } from '../types/api';
import { ENDPOINTS } from '../config/endpoints';

export class EmpresaService {
  static async list(): Promise<Empresa[]> {
    const response = await apiClient.request<ApiResponse<Empresa[]>>(ENDPOINTS.EMPRESAS.LIST);
    return response.data;
  }

  static async getById(id: string): Promise<Empresa> {
    const token = localStorage.getItem('authToken');
    const response = await apiClient.request<ApiResponse<Empresa>>(ENDPOINTS.EMPRESAS.BY_ID(id), {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return response.data;
  }

  static async create(empresa: Omit<Empresa, 'id'>): Promise<Empresa> {
    const response = await apiClient.request<ApiResponse<Empresa>>(ENDPOINTS.EMPRESAS.CREATE, {
      method: 'POST',
      body: JSON.stringify(empresa),
    });
    return response.data;
  }

  static async update(id: string, empresa: Partial<Empresa>): Promise<Empresa> {
    const response = await apiClient.request<ApiResponse<Empresa>>(ENDPOINTS.EMPRESAS.UPDATE(id), {
      method: 'PUT',
      body: JSON.stringify(empresa),
    });
    return response.data;
  }

  static async delete(id: string): Promise<void> {
    await apiClient.request(ENDPOINTS.EMPRESAS.DELETE(id), {
      method: 'DELETE',
    });
  }

  // Métodos específicos do domínio

  static async listByGrupo(grupoId: string): Promise<Empresa[]> {
    // Prefer using a dedicated endpoint if available; fallback to path composition
    // Check if ENDPOINTS.EMPRESAS has a BY_GRUPO helper; if not, compose the path
    const response = await apiClient.request<ApiResponse<Empresa[]>>(ENDPOINTS.EMPRESAS.BY_GRUPO(grupoId));
    return response.data;
  }

  static async updateSituacao(id: string, situacao: string): Promise<Empresa> {
    return this.update(id, { situacao });
  }

  static async searchByCnpj(cnpj: string): Promise<Empresa[]> {
    const query = `?cnpj=${encodeURIComponent(cnpj)}`;
    const endpoint = `${ENDPOINTS.EMPRESAS.SEARCH}${query}`;
    const response = await apiClient.request<ApiResponse<Empresa[]>>(endpoint);
    return response.data;
  }

  static async searchByName(nome: string): Promise<Empresa[]> {
    const query = `?nome=${encodeURIComponent(nome)}`;
    const endpoint = `${ENDPOINTS.EMPRESAS.SEARCH}${query}`;
    const response = await apiClient.request<ApiResponse<Empresa[]>>(endpoint);
    return response.data;
  }

  // Validação de dados
  static validateEmpresa(empresa: Partial<Empresa>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!empresa.nome || empresa.nome.trim().length < 2) {
      errors.push('Nome deve ter pelo menos 2 caracteres');
    }

    if (!empresa.cnpj || !this.isValidCnpj(empresa.cnpj)) {
      errors.push('CNPJ inválido');
    }

    if (!empresa.email || !this.isValidEmail(empresa.email)) {
      errors.push('Email inválido');
    }

    if (empresa.telefone && empresa.telefone.length > 0 && !this.isValidPhone(empresa.telefone)) {
      errors.push('Telefone inválido');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Utilitários de validação
  private static isValidCnpj(cnpj: string): boolean {
    // Remove caracteres não numéricos
    const cleanCnpj = cnpj.replace(/\D/g, '');
    
    // Verifica se tem 14 dígitos
    if (cleanCnpj.length !== 14) {
      return false;
    }

    // Verifica se não são todos dígitos iguais
    if (/^(\d)\1+$/.test(cleanCnpj)) {
      return false;
    }

    // Validação do algoritmo de CNPJ
    let sum = 0;
    let weight = 2;

    // Primeiro dígito verificador
    for (let i = 11; i >= 0; i--) {
      sum += parseInt(cleanCnpj.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }

    let remainder = sum % 11;
    let digit1 = remainder < 2 ? 0 : 11 - remainder;

    if (parseInt(cleanCnpj.charAt(12)) !== digit1) {
      return false;
    }

    // Segundo dígito verificador
    sum = 0;
    weight = 2;

    for (let i = 12; i >= 0; i--) {
      sum += parseInt(cleanCnpj.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }

    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;

    return parseInt(cleanCnpj.charAt(13)) === digit2;
  }

  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private static isValidPhone(phone: string): boolean {
    // Remove caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Verifica se tem 10 ou 11 dígitos (com ou sem nono dígito)
    return cleanPhone.length === 10 || cleanPhone.length === 11;
  }

  // Formatação de dados
  static formatCnpj(cnpj: string): string {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    if (cleanCnpj.length === 14) {
      return cleanCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  }

  static formatPhone(phone: string): string {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length === 11) {
      return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    if (cleanPhone.length === 10) {
      return cleanPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  }
}